import React, { useMemo } from "react";
import { Box } from "@mui/material";
import AcxCard from "components/UI/Cards/AcxCard";
import { SxProps, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import { SignalsTrendingIndicator } from "../SignalsTrendingIndicator";
import { observer } from "mobx-react";
import SmallCardStore, {
    AvailableOptionObjects,
    SmallCardState,
} from "./SmallCardStore";
import { SmallCardMenu } from "./SmallCardMenu";
import SmallCardSelectTitle from "./SmallCardSelectTitle";
import { defaultShadowsForSignals } from "components/Signals/SignalsUtils";
import { ObjectFilterIndicator } from "../ObjectFilters";
import PointMenu from "../Drilldown/PointMenu";
import DrilldownDialog from "../Drilldown/DrilldownDialog";

/**
 * Marks the controls that sit inside the card and own their own click: the title selector, the
 * object-filter indicator and the card menu. A click landing on one of these belongs to that
 * control, not to the card, so the drilldown menu stays closed. Matched by ancestry rather than by
 * stopping propagation in each control, so a control added later opts out by being marked.
 */
const INTERACTIVE_CHILD_SELECTOR = "[data-small-card-interactive]";

interface TitleProps {
    children: React.ReactNode;
    sx?: SxProps;
}

interface SmallCardProps {
    store: SmallCardStore;
    hideMenu?: boolean;
    readOnly?: boolean;
}

export const Title: React.FC<TitleProps> = ({ children, sx }) => (
    <Typography
        variant="h4"
        sx={{
            m: 0,
            p: theme.spacing(1, 0, 1, 2),
            lineHeight: "1.5",
            fontSize: "16px",
            fontWeight: 600,
            textAlign: "left",
            color: theme.palette.gray[500],
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            maxHeight: "52px",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
                display: "none",
            },
            ...sx,
        }}
    >
        {children}
    </Typography>
);

const SmallCard = observer(
    ({ store, hideMenu, readOnly }: SmallCardProps) => {
        const testId = `small-card-${store.dataEndpoint}`;

        const isLoading = store.getTaskLoading("refresh");

        const [pointMenuOpen, setPointMenuOpen] = React.useState(false);
        const [pointMenuAnchor, setPointMenuAnchor] = React.useState<
            { top: number; left: number } | undefined
        >(undefined);

        // readOnly covers the Add Cards preview, where the card is a sample rather than a reading
        // of the org's data and so has nothing to drill into.
        const canDrilldown =
            !readOnly &&
            !store.isPresentationMode &&
            !isLoading &&
            store.isPointMenuEnabled;

        const openPointMenu = (clientX: number, clientY: number) => {
            if (!canDrilldown) return;
            store.selectCard();
            setPointMenuAnchor({ top: clientY, left: clientX });
            setPointMenuOpen(true);
        };

        const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
            if (
                event.target instanceof Element &&
                event.target.closest(INTERACTIVE_CHILD_SELECTOR)
            ) {
                return;
            }
            openPointMenu(event.clientX, event.clientY);
        };

        const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            if (event.target !== event.currentTarget) return;
            event.preventDefault();
            // Keyboard activation has no cursor to anchor to, so the menu opens from the card.
            const bounds = event.currentTarget.getBoundingClientRect();
            openPointMenu(bounds.left + bounds.width / 2, bounds.bottom);
        };

        // Parse the available options from the backend
        const options = useMemo(() => {
            if (readOnly) {
                return [];
            }

            try {
                if (!store.availableOptionObjects) {
                    return [];
                }
                const parsedOptions: AvailableOptionObjects[] = JSON.parse(
                    store.availableOptionObjects || "[]",
                );

                const property = parsedOptions[0]?.property || "";
                const updatedState = {
                    [property]: parsedOptions[0].value,
                } as SmallCardState;

                // Only update the state if it is not already set
                if (!store.state) {
                    store.updateSmallCard({
                        state: {
                            ...updatedState,
                        },
                    });
                }
                return parsedOptions;
            } catch (error) {
                console.error("Error parsing options:", error);
                return [];
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            readOnly,
            store.availableOptionObjects,
            store.id,
            store.updateSmallCard,
        ]);

        return (
            // The menu and the dialog are siblings of the click target, not children of it. They
            // portal out of the DOM but stay inside the React tree, so a click on a menu item would
            // otherwise bubble back into the card's onClick and immediately reopen the menu that
            // choosing the item had just closed.
            <>
            <Box
                onClick={canDrilldown ? handleCardClick : undefined}
                onKeyDown={canDrilldown ? handleCardKeyDown : undefined}
                role={canDrilldown ? "button" : undefined}
                tabIndex={canDrilldown ? 0 : undefined}
                aria-haspopup={canDrilldown ? "menu" : undefined}
                data-testid={`${testId}-drilldown-target`}
                sx={{
                    width: "100%",
                    height: "100%",
                    cursor: canDrilldown ? "pointer" : "default",
                }}
            >
                <AcxCard
                    loading={isLoading}
                    loadingSize={24}
                    rootStyle={{
                        borderRadius: theme.shape.borderRadius * 2,
                        boxShadow: defaultShadowsForSignals,
                        width: "100%",
                        maxWidth: "100%",
                        height: "100%",
                    }}
                    title={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 1,
                                minWidth: 0,
                            }}
                            data-testid={`${testId}-title-wrapper`}
                        >
                            <Box
                                sx={{ flex: 1, minWidth: 0, maxHeight: "52px" }}
                                {...(!readOnly && options.length > 0
                                    ? { "data-small-card-interactive": true }
                                    : {})}
                            >
                                {!readOnly && options.length > 0 ? (
                                    <SmallCardSelectTitle
                                        store={store}
                                        options={options}
                                    />
                                ) : (
                                    <Title>{store.title}</Title>
                                )}
                            </Box>
                            {!hideMenu &&
                                !store.isPresentationMode &&
                                (store.isEditable || store.isDeletable) && (
                                    <Box
                                        data-small-card-interactive
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <ObjectFilterIndicator store={store} />
                                        <SmallCardMenu
                                            store={store}
                                            testId={testId}
                                        />
                                    </Box>
                                )}
                        </Box>
                    }
                    headerStyle={{ padding: theme.spacing(0.5, 0, 0) }}
                    mainContent={
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "end",
                                width: "100%",
                            }}
                            data-testid={`${testId}-main-content`}
                        >
                            {store.data && store.data.stat && (
                                <Typography
                                    data-testid={`${testId}-stat`}
                                    sx={{
                                        fontSize: "20px",
                                        fontWeight: 600,
                                        color: theme.palette.gray[600],
                                        lineHeight: "32px",
                                        width: "100%",
                                        textAlign: "left",
                                    }}
                                >
                                    {store.data.stat}
                                </Typography>
                            )}
                            {store.data && store.data.trendingProps && (
                                <SignalsTrendingIndicator
                                    data-testId={`${testId}-trending-indicator`}
                                    {...store.data.trendingProps}
                                />
                            )}
                        </Box>
                    }
                    mainContentStyle={{
                        paddingTop: 0,
                        paddingBottom: theme.spacing(2),
                        alignItems: "end",
                    }}
                />
            </Box>
            {canDrilldown && (
                <PointMenu
                    store={store}
                    open={pointMenuOpen}
                    anchorPosition={pointMenuAnchor}
                    onClose={() => setPointMenuOpen(false)}
                    hideSelectionValue
                />
            )}
            <DrilldownDialog
                baseObjectStore={store}
                drilldownStore={store.drilldownStore}
            />
            </>
        );
    },
);

export default SmallCard;
